/**
 * Sticky Add to Cart Bar - Bundle Product Handler
 * Handles bundle product add to cart functionality from the sticky ATC bar
 * 
 * Required data attributes on the script container:
 * - data-is-bundle: boolean
 * - data-bundle-products: JSON array of product variants
 * - data-bundle-quantities: JSON array of quantities
 * - data-bundle-discount: number
 * - data-variant-id: variant ID
 * - data-section-id: section ID
 * - data-product-title: product title
 * - data-inventory-quantities: JSON array of inventory quantities
 * - data-error-message: error message translation
 * - data-add-to-cart-text: add to cart button text translation
 */

(function() {
  // Get bundle data from the data container
  const dataContainer = document.querySelector('[data-sticky-atc-bundle-data]');
  if (!dataContainer) {
    return; // No bundle data container found
  }

  const isBundleProduct = dataContainer.dataset.isBundle === 'true';
  const bundleProducts = JSON.parse(dataContainer.dataset.bundleProducts || '[]');
  const bundleQuantities = JSON.parse(dataContainer.dataset.bundleQuantities || '[]');
  const bundleDiscount = parseFloat(dataContainer.dataset.bundleDiscount || '0');
  const variantId = dataContainer.dataset.variantId;
  const sectionId = dataContainer.dataset.sectionId;
  const productTitle = dataContainer.dataset.productTitle;
  const bundle_products_inventory_quantities = JSON.parse(dataContainer.dataset.inventoryQuantities || '[]');
  const errorMessage = dataContainer.dataset.errorMessage || 'Error adding to cart';
  const addToCartText = dataContainer.dataset.addToCartText || 'Add to cart';

  // Early exit if not a bundle product
  if (!isBundleProduct || !bundleProducts || bundleProducts.length === 0) {
    return;
  }

  // Cache DOM selectors
  const selectors = {
    quantityInput: `#Quantity-${sectionId}`,
    stickyAtcBar: 'sticky-atc-bar-minimalist, .sticky-atc-bar'
  };

  // Cache frequently accessed elements
  let cachedElements = {
    stickyAtcBar: null,
    submitButton: null,
    quantityInput: null
  };

  // Function to get or cache sticky ATC bar element
  function getStickyAtcBar() {
    if (!cachedElements.stickyAtcBar) {
      cachedElements.stickyAtcBar = document.querySelector('sticky-atc-bar-minimalist') || 
                                    document.querySelector('.sticky-atc-bar');
    }
    return cachedElements.stickyAtcBar;
  }

  // Function to get or cache submit button
  function getSubmitButton() {
    if (!cachedElements.submitButton) {
      const stickyAtcBar = getStickyAtcBar();
      cachedElements.submitButton = stickyAtcBar ? stickyAtcBar.querySelector('[name="add"]') : null;
    }
    return cachedElements.submitButton;
  }

  // Clear cache when needed
  function clearCache() {
    cachedElements = {
      stickyAtcBar: null,
      submitButton: null,
      quantityInput: null
    };
  }

  // Function to check bundle inventory availability
  function checkBundleInventory(userQuantity) {
    if (!bundleProducts || !bundleQuantities) return { valid: false, message: 'Bundle data not available' };

    for (let i = 0; i < bundleProducts.length; i++) {
      const variant = bundleProducts[i];
      const bundleQuantity = bundleQuantities[i] || 1;
      const requiredQuantity = bundleQuantity * userQuantity;

      const inventoryQuantity = bundle_products_inventory_quantities[i] || 0;

      if (requiredQuantity > inventoryQuantity) {
        return {
          valid: false,
          message: `Insufficient inventory for ${variant.title}. Required: ${requiredQuantity}, Available: ${inventoryQuantity}`
        };
      }
    }

    return { valid: true };
  }

  // Function to get maximum allowed quantity for bundle
  function getMaxBundleQuantity() {
    if (!bundleProducts || !bundleQuantities || bundleProducts.length === 0) return 0;

    let maxQuantity = Infinity;

    for (let i = 0; i < bundleProducts.length; i++) {
      const variant = bundleProducts[i];
      const bundleQuantity = bundleQuantities[i] || 1;
      const inventoryQuantity = bundle_products_inventory_quantities[i] || 0;
      const maxForThisProduct = Math.floor(inventoryQuantity / bundleQuantity);

      if (maxForThisProduct < maxQuantity) {
        maxQuantity = maxForThisProduct;
      }
    }

    return maxQuantity === Infinity ? 0 : maxQuantity;
  }

  // Custom Add to Cart Function for Bundle Products (optimized)
  function handleBundleAddToCart(event) {
    event.preventDefault();
    event.stopPropagation();

    // Get user selected quantity (cached query)
    if (!cachedElements.quantityInput) {
      cachedElements.quantityInput = document.querySelector(selectors.quantityInput) ||
                                     document.querySelector('input[name="quantity"]');
    }
    const quantityInput = cachedElements.quantityInput || { value: 1 };
    const userQuantity = parseInt(quantityInput.value) || 1;

    // Check inventory before proceeding
    const inventoryCheck = checkBundleInventory(userQuantity);
    if (!inventoryCheck.valid) {
      showMessage(inventoryCheck.message, 'error');
      return;
    }

    // Generate a unique bundle ID
    const bundleId = `bundle-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Store only component variant IDs (not full variant objects)
    const bundleProductsData = JSON.stringify(
      bundleProducts.map((variant) => variant && variant.id).filter(Boolean)
    );

    // Prepare cart items with bundle metafields as properties
    const items = bundleProducts.map((variant, index) => {
      const bundleQuantity = bundleQuantities && bundleQuantities[index] ? bundleQuantities[index] : 1;

      // Create properties object with all metafields
      const properties = {
        '_bundle_id': bundleId,
        '_is_bundle': 'true',
        '_variant_id': variantId.toString(),
        '_bundle_quantity': bundleQuantity.toString(),
        '_user_quantity': userQuantity.toString(),
        '_product_title': variant.title || 'Bundle Variant',
        '_product_id': variant.id.toString(),
        '_bundle_products_data': bundleProductsData,
        '_bundle_name': productTitle
      };

      // Add bundle discount if available
      if (bundleDiscount) {
        properties['_bundle_discount'] = bundleDiscount.toString();
        properties['_discount_value'] = bundleDiscount.toString();
      }

      return {
        id: variant.id,
        quantity: bundleQuantity * userQuantity,
        properties: properties
      };
    });

    // Add items to cart via AJAX
    addBundleToCart(items);
  }

  // Function to add bundle items to cart (optimized)
  async function addBundleToCart(items) {
    const submitButton = getSubmitButton();
    
    // Show loading state
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('btn--loading');
      const buttonText = submitButton.querySelector('.btn__text') || submitButton.querySelector('span');
      if (buttonText) {
        buttonText.textContent = 'Adding...';
      }
    }

    try {
      // Add items one by one to avoid inventory conflicts
      let successCount = 0;

      for (const item of items) {
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: item.id,
              quantity: item.quantity,
              properties: item.properties
            })
          });

          if (response.ok) {
            successCount++;
          }
        } catch (itemError) {
          // Continue with other items even if one fails
        }
      }

      if (successCount > 0) {
        // Update cart state and show cart drawer
        await updateCartStateAndShowDrawerSimple();
      } else {
        showMessage(errorMessage, 'error');
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.classList.remove('btn--loading');
          const buttonText = submitButton.querySelector('.btn__text') || submitButton.querySelector('span');
          if (buttonText) {
            buttonText.textContent = addToCartText;
          }
        }
      }

    } catch (error) {
      showMessage(errorMessage, 'error');
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.classList.remove('btn--loading');
        const buttonText = submitButton.querySelector('.btn__text') || submitButton.querySelector('span');
        if (buttonText) {
          buttonText.textContent = addToCartText;
        }
      }
    }
  }

  // Simple function to update cart state and show cart drawer (optimized)
  async function updateCartStateAndShowDrawerSimple() {
    try {
      // Get cart drawer section ID
      const cartDrawer = document.querySelector('cart-drawer');
      const cartDrawerSectionId = cartDrawer?.getAttribute('data-section-id');
      
      if (!cartDrawerSectionId) {
        return;
      }

      // Fetch both cart data and cart drawer section concurrently
      const [cartResponse, sectionResponse] = await Promise.all([
        fetch('/cart.js'),
        fetch(`/?section_id=${cartDrawerSectionId}`)
      ]);
      
      const cartData = await cartResponse.json();
      const sectionHtml = await sectionResponse.text();
      
      // Parse the section HTML to get the cart drawer content
      const parser = new DOMParser();
      const parsedSection = parser.parseFromString(sectionHtml, 'text/html');
      const cartDrawerSection = parsedSection.querySelector(`#shopify-section-${cartDrawerSectionId}`);
      
      // Create the sections object that the theme expects
      const sections = {};
      if (cartDrawerSection) {
        sections[`sections--${cartDrawerSectionId}__cart-drawer`] = cartDrawerSection.outerHTML;
      }
      
      // Combine cart data with sections
      const cartJson = {
        ...cartData,
        sections: sections
      };
      
      // Show cart drawer first, then update cart state
      if (cartDrawer?.show) {
        const submitButton = getSubmitButton();
        
        setTimeout(() => {
          if (submitButton) {
            cartDrawer.show(submitButton);
          } else {
            cartDrawer.show();
          }
          
          // Update cart drawer content and trigger events
          setTimeout(() => {
            updateCartDrawerContent(cartJson, cartDrawerSectionId);
            
            setTimeout(() => {
              updateCartBadge(cartJson);
              
              document.dispatchEvent(new CustomEvent('product-ajax:added', {
                detail: {
                  product: { status: 'success' },
                  cart: cartJson
                }
              }));
            }, 300);
            
          }, 200);
          
        }, 100);
      }
      
    } catch (error) {
      // Silent fail
    }
  }

  // Function to update cart drawer content directly (optimized)
  function updateCartDrawerContent(cartJson, cartDrawerSectionId) {
    try {
      const sectionKey = `sections--${cartDrawerSectionId}__cart-drawer`;
      const sectionContent = cartJson.sections?.[sectionKey];
      
      if (!sectionContent) return;
      
      // Get the cart drawer elements
      const cartDrawerBody = document.querySelector(`#CartDrawerBody-${cartDrawerSectionId}`);
      const cartDrawerFooter = document.querySelector(`#CartDrawerFooter-${cartDrawerSectionId}`);
      const cartDrawerEmpty = document.querySelector(`#CartDrawerEmpty-${cartDrawerSectionId}`);
      
      // Parse the section content once
      const parser = new DOMParser();
      const parsedSection = parser.parseFromString(sectionContent, 'text/html');
      
      // Update cart drawer body
      const newCartDrawerBody = parsedSection.querySelector(`#CartDrawerBody-${cartDrawerSectionId}`);
      if (newCartDrawerBody && cartDrawerBody) {
        cartDrawerBody.innerHTML = newCartDrawerBody.innerHTML;
      }
      
      // Update cart drawer footer
      const newCartDrawerFooter = parsedSection.querySelector(`#CartDrawerFooter-${cartDrawerSectionId}`);
      if (newCartDrawerFooter && cartDrawerFooter) {
        cartDrawerFooter.innerHTML = newCartDrawerFooter.innerHTML;
      }
      
      // Update cart drawer empty state
      const newCartDrawerEmpty = parsedSection.querySelector(`#CartDrawerEmpty-${cartDrawerSectionId}`);
      if (newCartDrawerEmpty && cartDrawerEmpty) {
        cartDrawerEmpty.innerHTML = newCartDrawerEmpty.innerHTML;
      }
      
      // Show/hide cart drawer sections based on cart state
      const hasItems = cartJson.item_count > 0;
      if (cartDrawerBody) cartDrawerBody.classList.toggle('hidden', !hasItems);
      if (cartDrawerFooter) cartDrawerFooter.classList.toggle('hidden', !hasItems);
      if (cartDrawerEmpty) cartDrawerEmpty.classList.toggle('hidden', hasItems);
      
    } catch (error) {
      // Silent fail
    }
  }

  // Function to update cart badge in header (optimized)
  function updateCartBadge(cartData) {
    const cartCountElements = document.querySelectorAll('cart-count');
    const itemCount = cartData.item_count || 0;
    const count = itemCount > 99 ? '99+' : itemCount.toString();
    const hasItems = itemCount > 0;

    cartCountElements.forEach(element => {
      let displayCount = element.dataset.type === 'blank' ? `(${count})` : count;

      if (element.classList.contains('cart-count--absolute')) {
        if (itemCount > 99) {
          element.innerHTML = `<span class="text-sm-extra">${displayCount}</span>`;
          element.classList.add('cart-count--small-medium');
        } else {
          element.innerText = displayCount;
          element.classList.remove('cart-count--small-medium');
        }
      } else {
        element.innerText = displayCount;
      }

      element.hidden = !hasItems;
    });

    // Update document class
    document.documentElement.classList.toggle('cart-has-items', hasItems);
  }

  // Function to show messages
  function showMessage(message, type = 'info') {
    const errorWrapper = document.querySelector('.product-form__error-message-wrapper');
    const errorMessageEl = document.querySelector('.product-form__error-message');

    if (errorWrapper && errorMessageEl) {
      errorMessageEl.textContent = message;
      errorWrapper.hidden = false;

      if (type === 'success') {
        errorMessageEl.style.color = 'green';
      } else if (type === 'warning') {
        errorMessageEl.style.color = 'orange';
      } else if (type === 'error') {
        errorMessageEl.style.color = 'red';
      } else {
        errorMessageEl.style.color = '';
      }

      setTimeout(() => {
        errorWrapper.hidden = true;
      }, 5000);
    } else {
      alert(message);
    }
  }

  // Optimized initialization function
  function initializeBundleProduct() {
    // Clear cache to ensure fresh elements
    clearCache();
    
    const submitButton = getSubmitButton();
    
    if (submitButton) {
      // Add event listener only once
      submitButton.addEventListener('click', handleBundleAddToCart, { once: false });
    }
  }
  
  // Optimized initialization - single call after DOM ready
  function initWhenReady() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeBundleProduct, { once: true });
    } else {
      initializeBundleProduct();
    }
    
    // Single delayed initialization as fallback
    setTimeout(initializeBundleProduct, 300);
  }
  
  initWhenReady();
})();
